import * as React from 'react';
import { Dialog } from '@ampm-7en-ai/ui/components/Dialog';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { getApiUrl, getAuthHeaders } from '@/utils/api-config';
import type { SuperAdminLLMProvider } from '@/hooks/useLLMProviders';

/**
 * Adding a model provider, or replacing the key on one that exists.
 *
 * The same dialog does both, because they are the same three fields — and
 * because an edit is mostly "the key rotated", which is easier to see when
 * it sits where the key was typed in the first place.
 */

/** The providers the platform knows, and what each one offers out of the box. */
const KNOWN = [
  { name: 'OpenAI', models: ['gpt-4o', 'gpt-4', 'gpt-3.5-turbo'] },
  { name: 'Anthropic', models: ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'] },
  { name: 'Google AI', models: ['gemini-pro', 'gemini-ultra'] },
  { name: 'Mistral AI', models: ['mistral-large', 'mistral-medium', 'mistral-small'] },
  { name: 'Groq', models: ['groq-1', 'groq-2'] },
];

/** The default model's name, whichever shape the API sent it in. */
const defaultModelName = (provider: SuperAdminLLMProvider | null | undefined): string => {
  const chosen = provider?.default_model;
  if (!chosen) return '';
  if (typeof chosen === 'string') return chosen;
  return chosen.name ?? '';
};

export default function ProviderDialogV2({
  open,
  onOpenChange,
  provider,
  onAdded,
  onUpdated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The one being changed, or null when a new one is being added. */
  provider?: SuperAdminLLMProvider | null;
  onAdded?: () => void;
  onUpdated?: (id: number, change: Partial<SuperAdminLLMProvider>) => Promise<unknown>;
}) {
  const { toast } = useToast();
  const { getToken } = useAuth();
  const editing = Boolean(provider);

  const [name, setName] = React.useState('');
  const [apiKey, setApiKey] = React.useState('');
  const [defaultModel, setDefaultModel] = React.useState('');
  const [active, setActive] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setName(provider?.provider_name ?? '');
    // Never prefilled: the stored key is write-only, and showing a
    // placeholder that looks like a value invites saving it back.
    setApiKey('');
    setDefaultModel(defaultModelName(provider));
    setActive(provider?.is_active ?? true);
    setSaving(false);
  }, [open, provider]);

  const known = KNOWN.find((one) => one.name === name);
  const blocked = !name.trim() || (!editing && !apiKey.trim());

  const save = async () => {
    if (blocked) return;
    setSaving(true);
    try {
      if (editing && provider) {
        const change: Partial<SuperAdminLLMProvider> = {
          provider_name: name,
          is_active: active,
          status: active ? 'active' : 'inactive',
          ...(defaultModel ? { default_model: defaultModel } : {}),
          // An empty box means the key it already has stays.
          ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
        };
        await onUpdated?.(provider.id, change);
        toast({ title: `${name} updated` });
      } else {
        const token = getToken();
        if (!token) throw new Error('You need to be signed in to add a provider.');

        const response = await fetch(getApiUrl('admin/provider-configs/'), {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify({
            provider_name: name,
            api_key: apiKey.trim(),
            default_model: defaultModel,
            is_active: active,
            status: active ? 'active' : 'inactive',
          }),
        });
        if (!response.ok) throw new Error('The provider wasn’t added.');

        const body = await response.json().catch(() => null);
        toast({ title: body?.message || `${name} added` });
        onAdded?.();
      }
      onOpenChange(false);
    } catch (failure) {
      toast({
        title: 'That didn’t work',
        description: failure instanceof Error ? failure.message : 'Nothing was saved.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `Edit ${provider?.provider_name ?? 'provider'}` : 'Add a provider'}
      description={
        editing
          ? 'The key is stored encrypted and never shown again. Leave it empty to keep the one it has.'
          : 'Its models become available to every workspace on a plan that allows them.'
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving || blocked}>
            {saving ? 'Saving…' : editing ? 'Save' : 'Add provider'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Provider" htmlFor="provider-name" hint="One of the ones the platform knows how to call.">
          {editing ? (
            <Input id="provider-name" value={name} onChange={(e) => setName(e.target.value)} />
          ) : (
            <Select
              id="provider-name"
              options={KNOWN.map((one) => ({ value: one.name, label: one.name }))}
              value={name}
              onValueChange={(value) => {
                setName(value);
                setDefaultModel('');
              }}
              placeholder="Choose a provider"
            />
          )}
        </Field>

        <Field
          label={editing ? 'New API key' : 'API key'}
          htmlFor="provider-key"
          hint={editing ? 'Leave empty to keep the key it already has.' : 'Stored encrypted; it never comes back to this page.'}
        >
          <Input
            id="provider-key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={editing ? '••••••••' : 'sk-…'}
          />
        </Field>

        {known && known.models.length > 0 && (
          <Field label="Default model" htmlFor="provider-default" hint="Used when an agent doesn’t name one.">
            <Select
              id="provider-default"
              options={known.models.map((model) => ({ value: model, label: model }))}
              value={defaultModel}
              onValueChange={setDefaultModel}
              placeholder="None"
            />
          </Field>
        )}

        {editing && !known && defaultModel && (
          <Field label="Default model" htmlFor="provider-default-free">
            <Input id="provider-default-free" value={defaultModel} onChange={(e) => setDefaultModel(e.target.value)} />
          </Field>
        )}

        <ToggleRow
          label="Available to workspaces"
          description="Switched off, no agent can reach its models whatever they are set to."
          checked={active}
          onCheckedChange={setActive}
        />

        {editing && !active && provider?.is_active && (
          <Callout tone="warning" title="This takes its models away">
            Agents set to one of them stop answering until it is switched back on.
          </Callout>
        )}
      </div>
    </Dialog>
  );
}
